import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const RecentCredentialsGrid = () => {
  const recentCredentials = [
    {
      id: 'cred-001',
      title: 'Software Engineering Certificate',
      issuer: 'TechCorp University',
      issuerLogo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=64&h=64&fit=crop&crop=center',
      type: 'Educational',
      status: 'verified',
      issuedDate: '2025-08-28',
      expiryDate: '2027-08-28',
      credentialHash: '0x7f9a2b8c...',
      verificationCount: 12
    },
    {
      id: 'cred-002',
      title: 'Professional Driver License',
      issuer: 'Department of Motor Vehicles',
      issuerLogo: 'https://images.pexels.com/photos/164634/pexels-photo-164634.jpeg?w=64&h=64&fit=crop&crop=center',
      type: 'Government',
      status: 'active',
      issuedDate: '2025-08-25',
      expiryDate: '2029-08-25',
      credentialHash: '0x3e5d1a9f...',
      verificationCount: 8
    },
    {
      id: 'cred-003',
      title: 'Medical Certification',
      issuer: 'Healthcare Board',
      issuerLogo: 'https://images.pixabay.com/photo/2017/09/07/08/54/money-2724241_1280.jpg?w=64&h=64&fit=crop&crop=center',
      type: 'Professional',
      status: 'pending',
      issuedDate: '2025-08-30',
      expiryDate: '2026-08-30',
      credentialHash: '0x1b4c8e2d...',
      verificationCount: 3
    },
    {
      id: 'cred-004',
      title: 'Digital Identity Proof',
      issuer: 'Identity Verification Service',
      issuerLogo: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=64&h=64&fit=crop&crop=center',
      type: 'Identity',
      status: 'verified',
      issuedDate: '2025-09-01',
      expiryDate: '2030-09-01',
      credentialHash: '0x9a7f3c1e...',
      verificationCount: 15
    },
    {
      id: 'cred-005',
      title: 'Blockchain Developer Badge',
      issuer: 'Crypto Academy',
      issuerLogo: 'https://images.pexels.com/photos/730547/pexels-photo-730547.jpeg?w=64&h=64&fit=crop&crop=center',
      type: 'Professional',
      status: 'verified',
      issuedDate: '2025-08-20',
      expiryDate: '2027-08-20',
      credentialHash: '0x5f2e9b8a...',
      verificationCount: 22
    },
    {
      id: 'cred-006',
      title: 'Security Clearance Level 2',
      issuer: 'Security Authority',
      issuerLogo: 'https://images.pixabay.com/photo/2016/04/04/14/12/monitor-1307227_1280.jpg?w=64&h=64&fit=crop&crop=center',
      type: 'Security',
      status: 'active',
      issuedDate: '2025-08-15',
      expiryDate: '2026-08-15',
      credentialHash: '0x8d1a4f7c...',
      verificationCount: 6
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'text-success bg-success/10';
      case 'active': return 'text-primary bg-primary/10';
      case 'pending': return 'text-warning bg-warning/10';
      case 'expired': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified': return 'CheckCircle';
      case 'active': return 'Shield';
      case 'pending': return 'Clock';
      case 'expired': return 'XCircle';
      default: return 'Circle';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Educational': return 'GraduationCap';
      case 'Government': return 'Building';
      case 'Professional': return 'Briefcase';
      case 'Identity': return 'User';
      case 'Security': return 'Lock';
      default: return 'FileText';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Recent Credentials</h2>
        <button className="text-primary hover:text-primary/80 text-sm font-medium transition-colors">
          View All
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {recentCredentials?.map((credential) => (
          <div key={credential?.id} className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow duration-200 cursor-pointer">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                  <Image 
                    src={credential?.issuerLogo} 
                    alt={credential?.issuer}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-foreground text-sm truncate">{credential?.title}</h3>
                  <p className="text-xs text-muted-foreground truncate">{credential?.issuer}</p>
                </div>
              </div>
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(credential?.status)}`}>
                <div className="flex items-center space-x-1">
                  <Icon name={getStatusIcon(credential?.status)} size={12} />
                  <span className="capitalize">{credential?.status}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center space-x-1 text-muted-foreground">
                  <Icon name={getTypeIcon(credential?.type)} size={12} />
                  <span>{credential?.type}</span>
                </div>
                <span className="text-muted-foreground">
                  {new Date(credential.issuedDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
              </div>
              
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Hash: {credential?.credentialHash}</span>
                <div className="flex items-center space-x-1 text-muted-foreground">
                  <Icon name="Eye" size={12} />
                  <span>{credential?.verificationCount}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-border">
              <div className="text-xs text-muted-foreground">
                Expires: {new Date(credential.expiryDate)?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </div>
              <div className="flex items-center space-x-2">
                <button className="p-1 hover:bg-muted rounded transition-colors">
                  <Icon name="Share" size={14} className="text-muted-foreground" />
                </button>
                <button className="p-1 hover:bg-muted rounded transition-colors">
                  <Icon name="MoreVertical" size={14} className="text-muted-foreground" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RecentCredentialsGrid;